import { defineComponent, h, nextTick, onActivated, onBeforeUnmount, onMounted, ref, watch, watchEffect } from 'vue';

import { classNames, getOffset, getScrollBarSize, addEventListenerWrapper } from '@components/utils';

import { useLayoutState } from '../../composable';

import type { Ref } from 'vue';

interface IStickyScrollbarProps {
  scrollBodyRef: Ref<HTMLElement | undefined>;
  offsetScroll: number;
  container: HTMLElement | Window | null;
  scrollBodySizeInfo: {
    scrollWidth: number;
    clientWidth: number;
  };
  onScroll: (params: { scrollLeft?: number }) => void;
}

export default defineComponent<IStickyScrollbarProps>({
  name: 'StickyScrollbar',
  inheritAttrs: false,
  props: ['scrollBodyRef', 'onScroll', 'offsetScroll', 'container', 'scrollBodySizeInfo'] as any,
  emits: ['scroll'],
  setup(props, { emit, expose }) {
    const bodyScrollWidth = ref(0);
    const bodyWidth = ref(0);
    const scrollbarWidth = ref(0);
    const scrollbarRef = ref();
    const isActive = ref(false);
    const refState = ref({ delta: 0, x: 0 });
    const [scrollState, setScrollState] = useLayoutState({
      scrollLeft: 0,
      isHiddenScrollbar: true
    });

    watchEffect(
      () => {
        bodyScrollWidth.value = props.scrollBodySizeInfo.scrollWidth || 0;
        bodyWidth.value = props.scrollBodySizeInfo.clientWidth || 0;
        scrollbarWidth.value = bodyScrollWidth.value && bodyWidth.value * (bodyWidth.value / bodyScrollWidth.value);
      },
      { flush: 'post' }
    );

    const onMouseUp: (event: MouseEvent) => void = () => {
      isActive.value = false;
    };

    const onMouseDown: (event: MouseEvent) => void = (event) => {
      refState.value = { delta: event.pageX - scrollState.value.scrollLeft, x: 0 };
      isActive.value = true;
      event.preventDefault();
    };

    const onMouseMove: (event: MouseEvent) => void = (event) => {
      // https://developer.mozilla.org/en-US/docs/Web/API/MouseEvent/buttons
      const { buttons } = event || (window?.event as any);

      if (!isActive.value || buttons === 0) {
        // If out body mouse up, we can set isActive false when mouse move
        if (isActive.value) {
          isActive.value = false;
        }
        return;
      }

      let left: number = refState.value.x + event.pageX - refState.value.x - refState.value.delta;

      if (left <= 0) {
        left = 0;
      }

      if (left + scrollbarWidth.value >= bodyWidth.value) {
        left = bodyWidth.value - scrollbarWidth.value;
      }

      emit('scroll', {
        scrollLeft: (left / bodyWidth.value) * (bodyScrollWidth.value + 2)
      });

      refState.value.x = event.pageX;
    };

    const onContainerScroll = () => {
      if (!props.scrollBodyRef.value) return;

      const tableOffsetTop = getOffset(props.scrollBodyRef.value).top;
      const tableBottomOffset = tableOffsetTop + props.scrollBodyRef.value.offsetHeight;
      const currentClientOffset =
        props.container === window
          ? document.documentElement.scrollTop + window.innerHeight
          : getOffset(props.container).top + (props.container as HTMLElement).clientHeight;

      if (
        tableBottomOffset - getScrollBarSize() <= currentClientOffset ||
        tableOffsetTop >= currentClientOffset - props.offsetScroll
      ) {
        setScrollState((state) => ({
          ...state,
          isHiddenScrollbar: true
        }));
      } else {
        setScrollState((state) => ({
          ...state,
          isHiddenScrollbar: false
        }));
      }
    };

    const setScrollLeft = (left: number) => {
      setScrollState((state) => ({
        ...state,
        scrollLeft: (left / bodyScrollWidth.value) * bodyWidth.value || 0
      }));
    };

    let onMouseUpListener: { remove: () => void } | null = null;
    let onMouseMoveListener: { remove: () => void } | null = null;
    let onResizeListener: { remove: () => void } | null = null;
    let onScrollListener: { remove: () => void } | null = null;

    onMounted(() => {
      onMouseUpListener = addEventListenerWrapper(document.body, 'mouseup', onMouseUp, false);
      onMouseMoveListener = addEventListenerWrapper(document.body, 'mousemove', onMouseMove, false);
      onResizeListener = addEventListenerWrapper(window, 'resize', onContainerScroll, false);
    });

    onActivated(() => {
      nextTick(() => {
        onContainerScroll();
      });
    });

    onMounted(() => {
      setTimeout(() => {
        watch(
          [scrollbarWidth, isActive],
          () => {
            onContainerScroll();
          },
          { immediate: true, flush: 'post' }
        );
      });
    });

    watch(
      () => props.container,
      () => {
        onScrollListener?.remove();
        onScrollListener = addEventListenerWrapper(props.container, 'scroll', onContainerScroll, false);
      },
      { immediate: true, flush: 'post' }
    );

    onBeforeUnmount(() => {
      onMouseUpListener?.remove();
      onMouseMoveListener?.remove();
      onScrollListener?.remove();
      onResizeListener?.remove();
    });

    watch(
      () => ({ ...scrollState.value }),
      (newState, prevState) => {
        if (newState.isHiddenScrollbar !== prevState?.isHiddenScrollbar && !newState.isHiddenScrollbar) {
          setScrollState((state) => {
            const bodyNode = props.scrollBodyRef.value;

            if (!bodyNode) return state;

            return {
              ...state,
              scrollLeft: (bodyNode.scrollLeft / bodyNode.scrollWidth) * bodyNode.clientWidth
            };
          });
        }
      },
      { immediate: true }
    );

    const scrollbarSize = getScrollBarSize();

    expose({ setScrollLeft });

    return () => {
      if (bodyScrollWidth.value <= bodyWidth.value || !scrollbarWidth.value || scrollState.value.isHiddenScrollbar) {
        return null;
      }

      return h(
        'div',
        {
          class: 'cds-table__sticky-scroll',
          style: {
            height: scrollbarSize + 'px',
            width: bodyWidth.value + 'px',
            bottom: props.offsetScroll + 'px'
          }
        },
        h('div', {
          ref: scrollbarRef,
          class: classNames('cds-table__sticky-scroll-bar', {
            [`cds-table__sticky-scroll-bar--active`]: isActive.value
          }),
          style: {
            width: scrollbarWidth.value + 'px',
            transform: `translateX(${scrollState.value.scrollLeft}px, 0, 0)`
          }
        })
      );
    };
  }
});
